import { create } from "zustand";
import { api } from "@/lib/api";
import { getCachedPermissions, isAnonymous } from "@/hooks/usePermissions";
import type {
  Analytics, FacilityInfo, Metrics, WaterfallRunRecord,
  CovenantObservation, IncidentRecord, EligibilityViews,
} from "@/pages/facility/facility.types";

const INCIDENT_ACTIVE_STATUSES = new Set(["OPEN", "IN_PROGRESS", "ESCALATED"]);

type PortfolioStore = {
  facilityId: string | null;
  facility: FacilityInfo | null;
  analytics: Analytics | null;
  facilityAnalytics: Analytics | null;
  metrics: Metrics | null;
  metricsHistory: Metrics[];
  snapshotDate: string | null;
  waterfallRuns: WaterfallRunRecord[] | null;
  waterfallRunsLoading: boolean;
  waterfallRunsError: string | null;
  covenantObservations: CovenantObservation[] | null;
  covenantIncidents: IncidentRecord[] | null;
  covenantDataLoading: boolean;
  covenantDataError: string | null;
  eligibilityViews: EligibilityViews | null;
  eligibilityViewsLoading: boolean;
  // Distinguishes "never fetched" from "fetched, no evaluation (404)" — both
  // leave eligibilityViews null.
  eligibilityViewsLoaded: boolean;
  loading: boolean;
  error: string;
  load: (id: string) => Promise<void>;
  loadSnapshot: (id: string, date: string) => Promise<void>;
  loadWaterfallRuns: (id: string, params?: { from?: string; to?: string }) => Promise<void>;
  loadCovenantData: (id: string) => Promise<void>;
  loadEligibilityViews: (id: string) => Promise<void>;
  refresh: (id: string) => Promise<void>;
  setSnapshotDate: (date: string | null) => void;
  reset: () => void;
};

/** The measurement in force at `date`: the latest run on or before it.
 *
 *  Not "the run of that exact date". A facility is measured when a tape lands,
 *  and the snapshot list carries every tape date of the book — so a date can be
 *  selectable and have no run of its own. What was known on that day is the last
 *  run before it, which is what somebody asking "where did this stand on the
 *  14th" means. Null before the first run: nothing was known yet.
 *
 *  The caller must show `metric.date`, because it can be earlier than the date
 *  asked for and a figure under the wrong date is the bug this replaces.
 */
export function metricInForce(history: Metrics[], date: string | null): Metrics | null {
  if (!date) return history.length ? history[history.length - 1] : null;
  let found: Metrics | null = null;
  for (const m of history) {
    if (m.date <= date) found = m;
    else break;
  }
  return found;
}

export const usePortfolioStore = create<PortfolioStore>((set, get) => ({
  facilityId: null,
  facility: null,
  analytics: null,
  facilityAnalytics: null,
  metrics: null,
  metricsHistory: [],
  snapshotDate: null,
  waterfallRuns: null,
  waterfallRunsLoading: false,
  waterfallRunsError: null,
  covenantObservations: null,
  covenantIncidents: null,
  covenantDataLoading: false,
  covenantDataError: null,
  eligibilityViews: null,
  eligibilityViewsLoading: false,
  eligibilityViewsLoaded: false,
  loading: false,
  error: "",

  load: async (id) => {
    if (get().facilityId === id && get().facility && get().analytics) return;
    set({ loading: true, error: "", facilityId: id });
    try {
      const hydrated = await api.hydrateFacility(id);
      set({ facility: hydrated.facility, metrics: hydrated.metrics, metricsHistory: hydrated.history, analytics: hydrated.analytics });
      const facilityAnalytics = hydrated.analytics
        ? await api.getFacilityAnalytics(id, undefined, true).catch(() => null)
        : null;
      set({ facilityAnalytics });
    } catch (e) {
      set({ error: String(e) });
    } finally {
      set({ loading: false });
    }
  },

  loadSnapshot: async (id, date) => {
    set({ loading: true, error: "", snapshotDate: date });
    try {
      const [analytics, facilityAnalytics] = await Promise.all([
        api.getFacilityAnalytics(id, date, false),
        // Historical dates may have no eligible-scope snapshot (HU-059.2,
        // forward-only) — a 404 here must not blank the whole snapshot load.
        api.getFacilityAnalytics(id, date, true).catch(() => null),
      ]);
      // The measurement of that date, not the latest one. Picking the snapshot
      // used to move only the analytics, so the page showed August's pool next
      // to September's OC with nothing saying they were different dates.
      //
      // Taken from `metricsHistory`, already loaded: the same rows the series
      // charts draw, so the header and the chart cannot disagree.
      set({ analytics, facilityAnalytics, metrics: metricInForce(get().metricsHistory, date) });
    } catch (e) {
      set({ error: String(e) });
    } finally {
      set({ loading: false });
    }
  },

  loadWaterfallRuns: async (id, params) => {
    set({ waterfallRunsLoading: true, waterfallRunsError: null });
    try {
      const resp = await api.getWaterfallRuns(id, params);
      set({ waterfallRuns: resp.runs });
    } catch (e) {
      // Global toast already fired; record the failure so the section can
      // render its own error state (ROADMAP #40). Keep last data.
      set({ waterfallRunsError: e instanceof Error ? e.message : String(e) });
    } finally {
      set({ waterfallRunsLoading: false });
    }
  },

  // Two independent calls, not one Promise.all. The observations series answers
  // any grant on the facility; the incident thread needs a *named* one, because an
  // incident's evidence carries file names and account references. Joined, the
  // 403 on the second blanked the first and the whole Covenants section
  // rendered as an error for a reader entitled to the covenant table.
  loadCovenantData: async (id) => {
    set({ covenantDataLoading: true, covenantDataError: null });
    const perms = getCachedPermissions();
    const anonymous = perms ? isAnonymous(perms, get().facility?.book_id ?? null) : false;

    const incidents = anonymous
      ? Promise.resolve([])
      : api.getFacilityIncidents(id, { incident_type: "COVENANT_BREACH" })
          // Active breaches need their evidence items (pool_delta) — only the
          // detail endpoint returns them.
          .then((resp) => Promise.all(
            resp.incidents.map((inc) =>
              INCIDENT_ACTIVE_STATUSES.has(inc.status)
                ? api.getIncidentDetail(id, inc.id).catch(() => inc)
                : Promise.resolve(inc),
            ),
          ))
          .catch(() => []);

    try {
      const obsResp = await api.covenantObservations(id);
      set({ covenantObservations: obsResp.observations });
    } catch (e) {
      // Global toast already fired; record the failure so the section can
      // render its own error state. Keep last data.
      set({ covenantDataError: e instanceof Error ? e.message : String(e) });
    }
    // An anonymous guest gets an empty list, which the section reads as "no
    // active incident chip" — the covenant table does not depend on it.
    set({ covenantIncidents: await incidents, covenantDataLoading: false });
  },

  loadEligibilityViews: async (id) => {
    set({ eligibilityViewsLoading: true });
    try {
      const views = await api.getEligibilityViews(id);
      set({ eligibilityViews: views });
    } catch {
      // 404 = no eligibility evaluation yet — the block shows a placeholder.
      set({ eligibilityViews: null });
    } finally {
      set({ eligibilityViewsLoading: false, eligibilityViewsLoaded: true });
    }
  },

  refresh: async (id) => {
    set({ facilityId: null });
    await get().load(id);
  },

  setSnapshotDate: (date) => set({ snapshotDate: date }),

  reset: () => set({
    facilityId: null, facility: null, analytics: null, facilityAnalytics: null,
    metrics: null, metricsHistory: [], snapshotDate: null,
    waterfallRuns: null, waterfallRunsLoading: false, waterfallRunsError: null,
    covenantObservations: null, covenantIncidents: null, covenantDataLoading: false, covenantDataError: null,
    eligibilityViews: null, eligibilityViewsLoading: false, eligibilityViewsLoaded: false,
    loading: false, error: "",
  }),
}));
